import { HTMLAttributes, Key, SyntheticEvent, useEffect, useMemo, useState } from "react";
import { AxiosResponse } from "axios";
import Autocomplete, {
  AutocompleteRenderInputParams,
  createFilterOptions,
} from "@mui/material/Autocomplete";
import TextField from "@mui/material/TextField";
import { getPricebooks } from "../../../services";
import { ApiResponse } from "../../../types/express-data.type";
import { PriceBookResponse } from "../../../types/price-book.type";

/** The API's page-size cap; one page holds the whole catalogue of a few dozen pricebooks. */
const PRICEBOOK_LOOKUP_LIMIT = 100;

interface PricebookList {
  rows: PriceBookResponse[];
  count: number;
}

interface PricebookCodeInputProps {
  value: string;
  error: string | null;
  helperText: string;
  disabled: boolean;
  onChange: (code: string) => void;
}

/**
 * Picks a pricebook by code. Free text is allowed on purpose: the ราคาหน้าซอง pricebook has
 * price rows but no pricebook master row, so it never appears among the options. The server
 * checks that the code exists.
 */
export default function PricebookCodeInput({
  value,
  error,
  helperText,
  disabled,
  onChange,
}: Readonly<PricebookCodeInputProps>) {
  const [pricebooks, setPricebooks] = useState<PriceBookResponse[]>([]);
  const [loading, setLoading] = useState<boolean>(true);

  useEffect(() => {
    let isLatest = true;
    const loadPricebooks = async () => {
      try {
        const response: AxiosResponse<ApiResponse<PricebookList>> =
          await getPricebooks(PRICEBOOK_LOOKUP_LIMIT);
        if (isLatest) setPricebooks(response.data.data?.rows ?? []);
      } catch {
        // Surfaced by the axios interceptor; the code can still be typed by hand.
      } finally {
        if (isLatest) setLoading(false);
      }
    };
    loadPricebooks();
    return () => {
      isLatest = false;
    };
  }, []);

  const codes = useMemo(() => pricebooks.map((pricebook) => pricebook.code), [pricebooks]);
  const nameByCode = useMemo(
    () => new Map(pricebooks.map((pricebook) => [pricebook.code, pricebook.name])),
    [pricebooks],
  );

  const optionText = (code: string) => {
    const name = nameByCode.get(code);
    return name ? `${code} · ${name}` : code;
  };

  // Typing a name finds its pricebook too, not only its code.
  const filterOptions = createFilterOptions<string>({ stringify: optionText });

  const handleInputChange = (_event: SyntheticEvent, text: string) => onChange(text);

  const renderOption = (
    { key, ...optionProps }: HTMLAttributes<HTMLLIElement> & { key: Key },
    code: string,
  ) => (
    <li key={key} {...optionProps}>
      {optionText(code)}
    </li>
  );

  const renderInput = (params: AutocompleteRenderInputParams) => (
    <TextField
      {...params}
      size="small"
      placeholder="เลือกหรือพิมพ์รหัสสมุดราคา"
      error={Boolean(error)}
      helperText={error ?? helperText}
    />
  );

  return (
    <Autocomplete
      freeSolo
      options={codes}
      inputValue={value}
      onInputChange={handleInputChange}
      filterOptions={filterOptions}
      renderOption={renderOption}
      renderInput={renderInput}
      loading={loading}
      disabled={disabled}
    />
  );
}
